import CloverWeb3Injected from '@clover-network/web-wallet-sdk';
import { networks } from '../constants/networks';
import { getAccounts } from './utils';
import { stateEventObserver } from './EventObserver';
import EventTypes from '../constants/eventTypes';
import { WALLET_TYPES } from '../constants/wallet';

const isCloverProviderApproved = async () => {
  try {
    if (!window.clover || !window.clover.enable) return true;

    if (window.clover.selectedAddress) return true;

    const account = await window.clover.request({ method: 'eth_requestAccounts', params: [] });
    return !!account;
  } catch (err) {
    return false;
  }
};

/**
 * If MetaMask privacy is on, opens MetaMask modal to whitelist it
 *
 * @return {Promise<*>}
 */
const cloverProviderApprove = async () => {
  if (window.clover) return window.clover.enable();
};

let interval: NodeJS.Timeout;

export const removeCloverProviderListeners = () => {
  if (window.clover?.removeAllListeners) {
    window.clover.removeAllListeners();
  }
  if (interval) {
    clearInterval(interval);
    interval = null;
  }
};

const subscribeCloverProviderEvents = async (networkId: number) => {
  const unlockedAccount = await getAccounts(window.clover);
  if (window.clover) window.clover.autoRefreshOnNetworkChange = false;
  if (window.clover?.on) {
    window.clover.on('accountsChanged', async (accounts: string[]) => {
      removeCloverProviderListeners();
      if (accounts[0]) {
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
        stateEventObserver.emit(EventTypes.CONNECT_REQUEST_COMPONENT, { networkId, connectionType: WALLET_TYPES.binance });
      } else {
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
      }
    });

    window.clover.on('chainChanged', async () => {
      removeCloverProviderListeners();
      stateEventObserver.emit(EventTypes.DISCONNECT, null);
      if (!document.hidden) {
        stateEventObserver.emit(EventTypes.CONNECT_REQUEST_COMPONENT, { networkId, connectionType: WALLET_TYPES.binance });
      }
    });

    window.clover.on('disconnect', () => {
      removeCloverProviderListeners();
      stateEventObserver.emit(EventTypes.DISCONNECT, null);
    });
  } else {
    interval = setInterval(async () => {
      const address = await getAccounts(window.clover);
      if (address?.toLowerCase() !== unlockedAccount?.toLowerCase()) {
        removeCloverProviderListeners();
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
        return clearInterval(interval);
      }
    }, 1000);
  }
};

const getCloverWebWalletProvider = async (networkId: number) => {
  const network = networks[networkId];
  if (!network) {
    return Promise.reject(new Error(`Network with id ${networkId} not found!`));
  }
  const devEnv = /(test|testnet)/gi.test(network.chainName);
  const clover = new CloverWeb3Injected({ zIndex: 9999, devEnv });
  await clover.init({ network: { chainId: network.chainId } });
  await clover.login();
  return { provider: clover.provider };
};

export const getCloverProvider = async (networkId: number) => {
  if (window.clover) {
    const approved = await isCloverProviderApproved();
    if (!approved) {
      await cloverProviderApprove();
    }
    if (await isCloverProviderApproved()) {
      const provider = window.clover;
      if (parseInt(provider.chainId, 16) !== networkId) {
        throw new Error(`Your wallet network is not the same as in the app. Please, switch to the ${networks[networkId]?.chainName} network`);
      }
      subscribeCloverProviderEvents(networkId);
      return { provider };
    }
    throw new Error('Clover is not approved!');
  }
  return getCloverWebWalletProvider(networkId);
};